<template lang="pug">
fieldset(
  :disabled="disabled"
)
  table.datepicker-range(:class="{disabled: disabled}")
    thead
      tr.range-header
        th
          span.btn-left.glyphicon.glyphicon-chevron-left(@click="showPreviousRange()" v-show="canShowPreviousRange")
        th.range-text(:colspan="rangeMode === 'day' ? 5 : 1" @click="increaseRangeMode()") {{ currentRangeText }}
        th
          span.btn-right.glyphicon.glyphicon-chevron-right(@click="showNextRange()" v-show="canShowNextRange")

      tr.week-header(v-if="rangeMode === 'day'")
        each w in ['M', 'T', 'W', 'T', 'F', 'S', 'S']
          th
            span.week-text= w

    tbody
      tr.date-range(v-if="rangeMode === 'day'" v-for="w in dateRange")
        td.date-text(v-for="d in w" :class="d.classes" @click="selectDate(d.date)") {{ d.text }}

      tr.month-range(v-if="rangeMode === 'month'" v-for="q in monthRange")
        td.month-text(v-for="m in q" :class="m.classes" @click="selectMonth(m.date)") {{ m.text }}
</template>

<script>
export default {
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },

        format: {
            type: String,
            default: 'YYYY-MM-DD',
        },

        // eslint-disable-next-line vue/require-prop-types
        min: {
            default: null,
            validator(value) {
                return (
                    value === null ||
                    value instanceof Date ||
                    typeof value === 'string' ||
                    value instanceof String ||
                    value instanceof moment
                );
            },
        },

        // eslint-disable-next-line vue/require-prop-types
        max: {
            default: null,
            validator(value) {
                return (
                    value === null ||
                    value instanceof Date ||
                    typeof value === 'string' ||
                    value instanceof String ||
                    value instanceof moment
                );
            },
        },

        range: {
            type: Boolean,
            default: false,
        },

        // eslint-disable-next-line vue/require-prop-types,vue/require-default-prop
        value: {
            required: false,
        },
    },

    data() {
        return {
            currentDate: null,
            dateRange: [],
            monthRange: [],
            rangeMode: 'day',
        };
    },

    computed: {
        canShowPreviousRange() {
            let min = this.min;

            if (min === null) {
                return true;
            }

            min = moment(min).startOf('month');

            if (this.rangeMode === 'day') {
                return moment(this.currentDate)
                    .subtract(1, 'month')
                    .isSameOrAfter(min);
            }

            if (this.rangeMode === 'month') {
                return moment(this.currentDate)
                    .subtract(1, 'year')
                    .isSameOrAfter(min);
            }

            return true;
        },

        canShowNextRange() {
            let max = this.max;

            if (max === null) {
                return true;
            }

            max = moment(max).endOf('month');

            if (this.rangeMode === 'day') {
                return moment(this.currentDate)
                    .add(1, 'month')
                    .isSameOrBefore(max);
            }

            if (this.rangeMode === 'month') {
                return moment(this.currentDate)
                    .add(1, 'year')
                    .isSameOrBefore(max);
            }

            return true;
        },

        currentRangeText() {
            if (this.rangeMode === 'month') {
                return this.currentDate.format('YYYY');
            }

            return this.currentDate.format('MMMM YYYY');
        },
    },

    watch: {
        currentDate() {
            this.generate();
        },

        max() {
            this.generate();
        },

        min() {
            this.generate();
        },

        rangeMode() {
            this.generate();
        },

        value() {
            this.generate();
        },
    },

    mounted() {
        this.generate();
    },

    created() {
        let min = this.min;

        if (!_.isNil(this.value)) {
            this.currentDate = moment(this.value);
        } else {
            /*
            if (min === null) {
                this.currentDate = moment();
            } else {
                this.currentDate = moment(min);
            }
            */

            this.currentDate = moment();
        }
    },

    methods: {
        dateIsWithinRange(date) {
            let range = moment.range(this.min, this.max);
            return range.contains(date);
        },

        monthIsWithinRange(date) {
            let min = this.min;
            let max = this.max;

            if (min !== null) {
                min = moment(min).startOf('month');
            }

            if (max !== null) {
                max = moment(max).endOf('month');
            }

            let range = moment.range(min, max);
            return range.contains(date);
        },

        increaseRangeMode() {
            if (this.rangeMode === 'day') {
                this.rangeMode = 'month';
            }
        },

        decreaseRangeMode() {
            if (this.rangeMode === 'month') {
                this.rangeMode = 'day';
            }
        },

        extraPreviousWeekEnabled() {
            return moment(this.currentDate)
                .startOf('month')
                .endOf('week')
                .subtract(4, 'day')
                .isSame(this.currentDate, 'month');
        },

        extraNextWeekEnabled() {
            return moment(this.currentDate)
                .endOf('month')
                .startOf('week')
                .add(4, 'day')
                .isSame(this.currentDate, 'month');
        },

        generate() {
            if (this.rangeMode === 'day') {
                this.generateDateRange();
            }

            if (this.rangeMode === 'month') {
                this.generateMonthRange();
            }
        },

        generateDateRange() {
            this.dateRange = [];

            let ranges = [];

            let start = moment(this.currentDate)
                .startOf('month')
                .startOf('week');

            let end = moment(this.currentDate)
                .endOf('month')
                .endOf('week');

            if (this.extraNextWeekEnabled()) {
                end.add(1, 'week');
            }

            let temp = moment.range(start, end);

            if (temp.diff('week') < 5) {
                end.add(1, 'week');
            }

            let range = moment.range(start, end);

            range.by('days', m => {
                ranges.push({
                    date: m.startOf('day'),
                    text: m.format('D'),
                    classes: this.getDateClass(m),
                });

                this.dateRange = _.chunk(ranges, 7);
            });
        },

        generateMonthRange() {
            this.monthRange = [];

            let ranges = [];

            let start = moment(this.currentDate)
                .startOf('year')
                .startOf('month');

            let end = moment(this.currentDate)
                .endOf('year')
                .endOf('month');

            let range = moment.range(start, end);

            range.by('months', m => {
                ranges.push({
                    date: m.startOf('month'),
                    text: m.format('MMM'),
                    classes: this.getMonthClass(m),
                });

                this.monthRange = _.chunk(ranges, 3);
            });
        },

        getDateClass(date) {
            let clazz = [];

            if (!this.currentDate.isSame(date, 'month')) {
                if (this.currentDate.isAfter(date)) {
                    clazz.push('day-previous-month');
                } else {
                    clazz.push('day-next-month');
                }
            }

            if (moment().isSame(date, 'day')) {
                clazz.push('day-today');
            }

            if (!this.range) {
                if (date.isSame(this.value, 'day')) {
                    clazz.push('day-selected', 'day-start', 'day-end');
                }
            }

            if (!this.dateIsWithinRange(date)) {
                clazz.push('day-disabled');
            }

            return clazz;
        },

        getMonthClass(date) {
            let clazz = [];

            if (moment().isSame(date, 'month')) {
                clazz.push('month-today');
            }

            if (!this.monthIsWithinRange(date)) {
                clazz.push('month-disabled');
            }

            return clazz;
        },

        getMinimumDate() {
            if (this.min != null) {
                return moment(this.min, this.format);
            }

            return null;
        },

        getMaximumDate() {
            if (this.max != null) {
                return moment(this.max, this.format);
            }

            return null;
        },

        moveDay(d) {
            let day = moment(this.value);

            if (_.isNil(day) || !day.isValid()) {
                day = moment();

                if (!this.dateIsWithinRange(day)) {
                    day = moment(this.getMinimumDate());
                }
            } else {
                day.add(d, 'day');
            }

            if (!this.dateIsWithinRange(day)) return;

            this.selectDate(day, false, 'key');
            this.navigateToDate(day);
        },

        navigateToDate(date) {
            if (!date) return;

            let month = date.month();
            let year = date.year();

            this.currentDate = moment(this.currentDate)
                .month(month)
                .year(year);
        },

        resetView() {
            this.rangeMode = 'day';

            if (!_.isNil(this.value)) {
                this.navigateToDate(moment(this.value));
            }
        },

        selectDate(date, clear = true, source = 'mouse') {
            if (!this.dateIsWithinRange(date)) {
                return;
            }

            if (clear && date.isSame(this.value, 'day')) {
                this.$emit('input', null, source);
                return;
            }

            if (!this.range) {
                this.$emit('input', date, source);
            }
        },

        selectDayAfter() {
            this.moveDay(1);
        },

        selectDayBefore() {
            this.moveDay(-1);
        },

        selectWeekAfter() {
            this.moveDay(7);
        },

        selectWeekBefore() {
            this.moveDay(-7);
        },

        selectMonth(month) {
            if (!this.monthIsWithinRange(month)) {
                return;
            }

            this.decreaseRangeMode();
            this.currentDate = month;
        },

        showPreviousRange() {
            if (this.rangeMode === 'day') {
                this.currentDate = moment(this.currentDate).subtract(
                    1,
                    'month',
                );
            }

            if (this.rangeMode === 'month') {
                this.currentDate = moment(this.currentDate).subtract(1, 'year');
            }
        },

        showNextRange() {
            if (this.rangeMode === 'day') {
                this.currentDate = moment(this.currentDate).add(1, 'month');
            }

            if (this.rangeMode === 'month') {
                this.currentDate = moment(this.currentDate).add(1, 'year');
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../../sass/mixins';

%body {
    border-radius: 4px;
    height: 28px;
    line-height: 28px;
    text-align: center;

    @include user-select(none);
}

%body-hover {
    @extend %body;
    cursor: pointer;

    &:hover {
        background-color: #eeeeee;
    }
}

.datepicker-range {
    width: 100%;

    &.disabled {
        opacity: 0.65;

        .btn-left,
        .btn-right,
        .range-text,
        .date-text {
            cursor: not-allowed;
            pointer-events: none;

            &:hover {
                background-color: inherit;
            }
        }
    }
}

.range-header {
    .btn-left,
    .btn-right {
        @extend %body-hover;
        width: 100%;

        &.glyphicon {
            top: 0;
        }
    }

    .range-text {
        @extend %body-hover;
        font-weight: bold;
        margin-bottom: 0;
    }
}

.week-header {
    th {
        text-align: center;
        width: 14.285%;
    }

    .week-text {
        @extend %body;
        font-size: 10px;
        font-weight: bold;
        text-transform: uppercase;
    }
}

.date-range {
    .date-text {
        @extend %body-hover;
    }
}

.month-range {
    .month-text {
        @extend %body-hover;
        width: 33.333%;
    }
}

.day-previous-month,
.day-next-month {
    color: #999;
}

.day-selected {
    background-color: #0cc2aa;
    border-radius: 0 !important;
    color: #fff;

    &:hover {
        background-color: #0cc2aa !important;
        box-shadow: inset 0 -10rem 0 rgba(158, 158, 158, 0.2);
    }
}

.day-start {
    border-bottom-left-radius: 4px !important;
    border-top-left-radius: 4px !important;
}

.day-end {
    border-bottom-right-radius: 4px !important;
    border-top-right-radius: 4px !important;
}

.day-disabled,
.month-disabled {
    color: #ddd !important;
    cursor: not-allowed !important;

    &:hover {
        background-color: transparent !important;
    }
}

.day-today,
.month-today {
    font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/datepicker/datepicker-range.vue